import React, { useState, useContext } from 'react';
import { ProductContext } from '../../providers/ProductContext';
import { AuthContext } from '../../providers/AuthContext';
import { showError, showSuccess, showDeleteConfirm } from '../../utils/sweetAlert';
import { getAdminAuthHeaders } from '../../utils/adminApi';
import { CategoryContext } from '../../providers/CategoryContext';

const BulkVariantManager = () => {
  const { products, refetchProducts } = useContext(ProductContext);
  const { categories } = useContext(CategoryContext);
  const { token } = useContext(AuthContext);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedSizes, setSelectedSizes] = useState([]);
  const [selectedColors, setSelectedColors] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isCleaning, setIsCleaning] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');

  const availableSizes = ['S', 'M', 'L', 'XL', 'XXL'];

  const availableColors = [
    { name: 'Negro', value: 'Negro' },
    { name: 'Blanco', value: 'Blanco' },
    { name: 'Azul Marino', value: 'Azul Marino' },
    { name: 'Azul Oscuro', value: 'Azul Oscuro' },
    { name: 'Bordo', value: 'Bordo' },
    { name: 'Gris Oscuro', value: 'Gris Oscuro' },
    { name: 'Gris', value: 'Gris' },
    { name: 'Bordo Jaspe', value: 'Bordo Jaspe' },
    { name: 'Azul Acero', value: 'Azul Acero' },
    { name: 'Verde Jaspe', value: 'Verde Jaspe' },
    { name: 'Negro Jaspe', value: 'Negro Jaspe' }
  ];

  const productsWithoutVariants = products.filter(product =>
    !product.hasVariants && (!product.variants || product.variants.length === 0)
  );

  const filteredProducts = productsWithoutVariants.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategory === '' ||
      product.category?.name === selectedCategory ||
      product.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const handleProductSelect = (productId) => {
    setSelectedProducts(prev =>
      prev.includes(productId) ? prev.filter(id => id !== productId) : [...prev, productId]
    );
  };

  const handleSelectAll = () => {
    if (selectedProducts.length === filteredProducts.length) setSelectedProducts([]);
    else setSelectedProducts(filteredProducts.map(p => p._id));
  };

  const handleSizeToggle = (size) => {
    setSelectedSizes(prev => prev.includes(size) ? prev.filter(s => s !== size) : [...prev, size]);
  };

  const handleColorToggle = (color) => {
    setSelectedColors(prev => prev.includes(color) ? prev.filter(c => c !== color) : [...prev, color]);
  };

  const generateVariants = (basePrice, productId) => {
    const variants = [];
    const timestamp = Date.now();
    selectedSizes.forEach((size, sizeIndex) => {
      selectedColors.forEach((color, colorIndex) => {
        const uniqueId = `${productId.slice(-6)}-${timestamp}-${sizeIndex}${colorIndex}`;
        variants.push({ size, color, price: basePrice, sku: `${size}-${color}-${uniqueId}`.toLowerCase().replace(/\s+/g, '-'), isActive: true });
      });
    });
    return variants;
  };

  const handleApplyVariants = async () => {
    if (selectedProducts.length === 0) { showError('Selecciona al menos un producto'); return; }
    if (selectedSizes.length === 0 || selectedColors.length === 0) { showError('Selecciona al menos un tamaño y un color'); return; }
    setIsProcessing(true);
    try {
      const updatePromises = selectedProducts.map(async (productId) => {
        const product = products.find(p => p._id === productId);
        const basePrice = product.basePrice || product.price || 1000;
        const variants = generateVariants(basePrice, productId);
        const updatedProduct = {
          name: product.name, basePrice, discount: product.discount || 0, description: product.description,
          category: product.category, subcategory: product.subcategory, hasVariants: true, variants,
          size: selectedSizes, color: selectedColors, material: product.material, brand: product.brand,
          weight: product.weight, dimensions: product.dimensions, tags: product.tags, isActive: product.isActive
        };
        const response = await fetch(`${process.env.REACT_APP_API_URL}/products/${productId}`, {
          method: 'PUT', headers: getAdminAuthHeaders(token, { 'Content-Type': 'application/json' }),
          body: JSON.stringify(updatedProduct)
        });
        if (!response.ok) { const errorText = await response.text(); throw new Error(`Error actualizando ${product.name}: ${errorText}`); }
        return response;
      });
      await Promise.all(updatePromises);
      await refetchProducts();
      showSuccess(`${selectedProducts.length} productos actualizados con variantes`);
      setSelectedProducts([]); setSelectedSizes([]); setSelectedColors([]);
    } catch (error) {
      showError('Hubo un problema al agregar las variantes');
    } finally { setIsProcessing(false); }
  };

  const handleCleanVariants = async () => {
    const result = await showDeleteConfirm('¿Estás seguro de que quieres limpiar todas las variantes duplicadas? Esto eliminará las variantes existentes pero preservará las imágenes.');
    if (!result.isConfirmed) return;
    setIsCleaning(true);
    try {
      const productsWithVariants = products.filter(product => product.hasVariants && product.variants?.length > 0);
      let totalCleaned = 0;
      for (const product of productsWithVariants) {
        try {
          const cleanedProduct = { ...product, hasVariants: false };
          delete cleanedProduct.variants;
          const response = await fetch(`${process.env.REACT_APP_API_URL}/products/${product._id}`, {
            method: 'PUT', headers: getAdminAuthHeaders(token, { 'Content-Type': 'application/json' }),
            body: JSON.stringify(cleanedProduct)
          });
          if (response.ok) totalCleaned++;
        } catch (error) { /* skip */ }
      }
      showSuccess(`Limpieza completada. ${totalCleaned} productos procesados.`);
      await refetchProducts();
    } catch (error) {
      showError('Hubo un problema al limpiar las variantes');
    } finally { setIsCleaning(false); }
  };

  const totalVariantsToCreate = selectedProducts.length * selectedSizes.length * selectedColors.length;

  return (
    <div className="variantmgr-panel">
      {/* Header */}
      <div className="variantmgr-header">
        <div>
          <h1>Gestor de Variantes Masivo</h1>
          <p>Agrega talles y colores a productos sin variantes (principalmente remeras)</p>
        </div>
        <button className="variantmgr-clean-btn" onClick={handleCleanVariants} disabled={isCleaning}>
          {isCleaning ? <><i className="fas fa-spinner fa-spin"></i> Limpiando...</> : <><i className="fas fa-broom"></i> Limpiar Variantes</>}
        </button>
      </div>

      {/* KPIs */}
      <div className="variantmgr-kpi-row">
        <div className="variantmgr-kpi">
          <div className="variantmgr-kpi-icon" style={{ background: 'rgba(59,130,246,0.1)', color: '#3b82f6' }}>
            <i className="fas fa-box-open"></i>
          </div>
          <div className="variantmgr-kpi-info">
            <span className="variantmgr-kpi-value">{productsWithoutVariants.length}</span>
            <span className="variantmgr-kpi-label">Sin Variantes</span>
          </div>
        </div>
        <div className="variantmgr-kpi">
          <div className="variantmgr-kpi-icon" style={{ background: 'rgba(16,185,129,0.1)', color: '#10b981' }}>
            <i className="fas fa-check-square"></i>
          </div>
          <div className="variantmgr-kpi-info">
            <span className="variantmgr-kpi-value">{selectedProducts.length}</span>
            <span className="variantmgr-kpi-label">Seleccionados</span>
          </div>
        </div>
        <div className="variantmgr-kpi">
          <div className="variantmgr-kpi-icon" style={{ background: 'rgba(245,158,11,0.1)', color: '#f59e0b' }}>
            <i className="fas fa-ruler"></i>
          </div>
          <div className="variantmgr-kpi-info">
            <span className="variantmgr-kpi-value">{selectedSizes.length} × {selectedColors.length}</span>
            <span className="variantmgr-kpi-label">Talles × Colores</span>
          </div>
        </div>
        <div className="variantmgr-kpi">
          <div className="variantmgr-kpi-icon" style={{ background: 'rgba(139,92,246,0.1)', color: '#8b5cf6' }}>
            <i className="fas fa-layer-group"></i>
          </div>
          <div className="variantmgr-kpi-info">
            <span className="variantmgr-kpi-value">{totalVariantsToCreate}</span>
            <span className="variantmgr-kpi-label">Variantes a Crear</span>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="variantmgr-toolbar">
        <div className="variantmgr-search">
          <i className="fas fa-search"></i>
          <input type="text" placeholder="Buscar producto..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </div>
        <select className="variantmgr-select" value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}>
          <option value="">Todas las categorías</option>
          {categories.map(cat => <option key={cat._id} value={cat.name}>{cat.name}</option>)}
        </select>
        <span className="variantmgr-counter">{filteredProducts.length} productos</span>
      </div>

      {filteredProducts.length === 0 ? (
        <div className="variantmgr-empty">
          <i className="fas fa-inbox"></i>
          <h3>{productsWithoutVariants.length === 0 ? 'No hay productos sin variantes' : 'Sin resultados para los filtros'}</h3>
          <p>{productsWithoutVariants.length === 0 ? 'Todos los productos ya tienen variantes configuradas.' : 'Ajusta los filtros para encontrar productos.'}</p>
        </div>
      ) : (
        <div className="variantmgr-content">
          {/* Product List */}
          <div className="variantmgr-products-card">
            <div className="variantmgr-products-header">
              <h3>Productos sin Variantes</h3>
              <button className="variantmgr-select-all-btn" onClick={handleSelectAll}>
                {selectedProducts.length === filteredProducts.length ? 'Deseleccionar' : 'Seleccionar Todo'}
              </button>
            </div>
            <div className="variantmgr-products-list">
              {filteredProducts.map(product => (
                <label key={product._id} className={`variantmgr-product-item ${selectedProducts.includes(product._id) ? 'selected' : ''}`}>
                  <input type="checkbox" checked={selectedProducts.includes(product._id)} onChange={() => handleProductSelect(product._id)} />
                  <span className="variantmgr-product-check"><i className="fas fa-check"></i></span>
                  <div className="variantmgr-product-info">
                    <strong>{product.name}</strong>
                    <small>${product.basePrice || product.price} · {product.category?.name || product.category}</small>
                  </div>
                </label>
              ))}
            </div>
          </div>

          {/* Sizes & Colors */}
          <div className="variantmgr-options-row">
            <div className="variantmgr-option-card">
              <h3><i className="fas fa-ruler-combined"></i> Talles</h3>
              <div className="variantmgr-chips">
                {availableSizes.map(size => (
                  <button key={size} className={`variantmgr-chip ${selectedSizes.includes(size) ? 'active' : ''}`}
                    onClick={() => handleSizeToggle(size)}>{size}</button>
                ))}
              </div>
              <small>{selectedSizes.length > 0 ? selectedSizes.join(', ') : 'Ninguno seleccionado'}</small>
            </div>
            <div className="variantmgr-option-card">
              <h3><i className="fas fa-palette"></i> Colores</h3>
              <div className="variantmgr-chips">
                {availableColors.map(color => (
                  <button key={color.value} className={`variantmgr-chip ${selectedColors.includes(color.value) ? 'active' : ''}`}
                    onClick={() => handleColorToggle(color.value)}>{color.name}</button>
                ))}
              </div>
              <small>{selectedColors.length > 0 ? selectedColors.join(', ') : 'Ninguno seleccionado'}</small>
            </div>
          </div>

          {/* Summary & Apply */}
          <div className="variantmgr-summary-card">
            <div className="variantmgr-summary-items">
              <div className="variantmgr-summary-item">
                <span className="variantmgr-summary-label">Productos</span>
                <span className="variantmgr-summary-value">{selectedProducts.length}</span>
              </div>
              <div className="variantmgr-summary-divider">×</div>
              <div className="variantmgr-summary-item">
                <span className="variantmgr-summary-label">Talles</span>
                <span className="variantmgr-summary-value">{selectedSizes.length}</span>
              </div>
              <div className="variantmgr-summary-divider">×</div>
              <div className="variantmgr-summary-item">
                <span className="variantmgr-summary-label">Colores</span>
                <span className="variantmgr-summary-value">{selectedColors.length}</span>
              </div>
              <div className="variantmgr-summary-divider">=</div>
              <div className="variantmgr-summary-item highlight">
                <span className="variantmgr-summary-label">Total Variantes</span>
                <span className="variantmgr-summary-value">{totalVariantsToCreate}</span>
              </div>
            </div>
            <button className="variantmgr-apply-btn" onClick={handleApplyVariants}
              disabled={isProcessing || selectedProducts.length === 0 || selectedSizes.length === 0 || selectedColors.length === 0}>
              {isProcessing ? <><i className="fas fa-spinner fa-spin"></i> Procesando...</> : <><i className="fas fa-check"></i> Aplicar Variantes</>}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default BulkVariantManager;
